/**
 * Subscription status types for users
 */
export type SubscriptionStatus =
  | 'grandfathered'  // Lifetime free access (for existing 170 users)
  | 'premium'        // Active RevenueCat subscription
  | 'free'           // No subscription, limited features
  | 'trial';         // Trial period (optional future use)

/**
 * User profile document stored at users/{uid}
 * Contains subscription and account metadata
 */
export interface UserProfile {
  /** Firebase Auth UID */
  uid: string;

  /** User's email address */
  email?: string;

  /** Timestamp when the user account was created */
  createdAt: number;

  /** Whether this user was grandfathered in with lifetime free access */
  isGrandfathered: boolean;

  /** Timestamp when the user was marked as grandfathered (if applicable) */
  grandfatheredAt?: number;

  /** Current subscription status */
  subscriptionStatus: SubscriptionStatus;

  /** RevenueCat customer ID (set after RevenueCat initialization) */
  revenueCatId?: string;

  /** Last time the subscription status was verified with RevenueCat */
  lastSubscriptionCheck?: number;

  /** Whether the user has completed the first-time premium onboarding flow */
  hasSeenPremiumOnboarding?: boolean;
}

/**
 * Helper function to check if a user has premium access
 * (either grandfathered or active subscription)
 */
export function hasPremiumAccess(profile: UserProfile | null): boolean {
  if (!profile) return false;
  return profile.isGrandfathered || 
         profile.subscriptionStatus === 'premium' || 
         profile.subscriptionStatus === 'grandfathered';
}

/**
 * Helper function to create a default profile for new users
 */
export function createDefaultUserProfile(uid: string, email?: string): UserProfile {
  return {
    uid,
    email,
    createdAt: Date.now(),
    isGrandfathered: false,
    subscriptionStatus: 'free',
  };
}

/**
 * Helper function to create a grandfathered profile for existing users
 * Used by migration script
 */
export function createGrandfatheredUserProfile(uid: string, email?: string, originalCreatedAt?: number): UserProfile {
  return {
    uid,
    email,
    createdAt: originalCreatedAt || Date.now(),
    isGrandfathered: true,
    grandfatheredAt: Date.now(),
    subscriptionStatus: 'grandfathered',
  };
}

